// Structured according to https://egghead.io/lessons/javascript-redux-colocating-selectors-with-reducers

import appInitial from '@web-platform/shared-universal-redux/src/state/appInitial';
import combinedExplore from '@web-platform/shared-universal-redux/src/state/combinedExplore';
import * as fromFlightSearchResults from '@web-platform/shared-universal-redux/src/state/shared/flightSearchResults';

import combinedResults from './combinedResults';
import shared, * as fromShared from './shared';

import type {
  SkyscannerMichelinSearchV1Carhireindicativepricesection,
  SkyscannerMichelinSearchV1Hotelindicativepricesection,
} from '@skyscanner-internal/michelin-web-http-client';
import type { CarHireState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/carHire';
import type { CombinedComponentKeyState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/combinedComponentKeys';
import type { PollingSessionState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/michelin/michelin';
import type { SearchControlsState } from '@web-platform/shared-universal-types/src/redux/banana/state/shared/searchControls';
import type {
  State,
  StaticReducers,
} from '@web-platform/shared-universal-types/src/redux/banana/state/state-banana-types';
import type { SelectedSortState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/sort/sort';
import type { PackagesRealTimeSavingsState } from '@web-platform/shared-universal-types/src/redux/state/shared/michelin/packagesRealTimeSavings';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

type HotelIndicativePricesSection =
  SkyscannerMichelinSearchV1Hotelindicativepricesection.HotelIndicativePricesSection;
type CarHireIndicativePricesSection =
  SkyscannerMichelinSearchV1Carhireindicativepricesection.CarHireIndicativePricesSection;

export const staticReducers: StaticReducers = {
  appInitial,
  combinedExplore,
  combinedResults,
  shared,
};

// shared - combined results & explore
export const getCombinedComponentKeys = (state: State): CombinedComponentKeyState[] =>
  fromShared.getCombinedComponentKeys(state.shared);
export const isLoadingCombinedComponentKeys = (state: State): boolean =>
  fromShared.isLoadingCombinedComponentKeys(state.shared);
// shared - radar
export const getLastFlightsItinerariesRendered = (state: State): boolean =>
  fromShared.getLastFlightsItinerariesRendered(state.shared);
export const isLoadingRadarMutation = (state: State): boolean =>
  fromShared.isLoadingRadarMutation(state.shared);
// shared - yolo
export const isYoloReadyToDisplay = (state: State): boolean =>
  fromShared.isReadyToDisplay(state.shared);
export const isCookieBannerClosed = (state: State): boolean =>
  fromShared.isCookieBannerClosed(state.shared);
// shared - searchControls
export const showSearchControls = (state: State): boolean =>
  fromShared.showSearchControls(state.shared);
export const getUserRequestedSearch = (state: State): boolean =>
  fromShared.getUserRequestedSearch(state.shared);
export const getSearchControls = (state: State): SearchControlsState =>
  fromShared.getSearchControlsState(state.shared);
// shared - saveToList
export const getS2lNotification = (state: State) => fromShared.getS2lNotification(state.shared);
export const getLastUpdatedActionId = (state: State): Nullable<string> =>
  fromShared.getLastUpdatedActionId(state.shared);
export const getStatusText = (state: State): Nullable<string> =>
  fromShared.getStatusText(state.shared);
export const getErrorDialogText = (state: State) => fromShared.getErrorDialogText(state.shared);
// shared - michelin
export const getMichelinPollingState = (state: State): PollingSessionState =>
  fromShared.getMichelinPollingState(state.shared);
export const getMichelinCarHire = (state: State): CarHireState =>
  fromShared.getMichelinCarHire(state.shared);
export const getMichelinHotels = (state: State) => fromShared.getMichelinHotels(state.shared);
export const getMichelinVerticals = (state: State) => fromShared.getMichelinVerticals(state.shared);
export const getHotelIndicativePricing = (state: State): HotelIndicativePricesSection =>
  fromShared.getHotelIndicativePricing(state.shared);
export const getCarHireIndicativePricing = (state: State): CarHireIndicativePricesSection =>
  fromShared.getCarHireIndicativePricing(state.shared);
export const getExploreAccommodations = (state: State) =>
  fromShared.getExploreAccommodations(state.shared);
export const getPackagesRealTimeSavings = (state: State): PackagesRealTimeSavingsState =>
  fromShared.getPackagesRealTimeSavings(state.shared);
// shared - fps
export const hadCompleteFpsResponse = (state: State): boolean =>
  fromShared.hadCompleteFpsResponse(state.shared);

// // flightSearchResult - sort
export const getSelectedSort = (state: State): SelectedSortState =>
  fromShared.getSelectedSort(state.shared);
// FlightSearchResults - filters
export const getAirportsFilter = (state: State) =>
  fromFlightSearchResults.getAirportsFilter(state.shared.flightSearchResults);

// layovers
export const getLayoversDuration = (state: State) =>
  fromFlightSearchResults.getLayoversDurationFilter(state.shared.flightSearchResults);
export const getLayoversExcludedAirports = (state: State) =>
  fromFlightSearchResults.getLayoversExcludedAirportsFilter(state.shared.flightSearchResults);

// flightSearchResult - filterOrSortChangedCount
export const getFilterOrSortChangedCount = (state: State) =>
  fromFlightSearchResults.getFilters(state.shared.flightSearchResults).filterOrSortChangedCount;
export const allSponsoredContent = (state: State) => fromShared.allSponsoredContent(state.shared);
export const getSponsoredAgent = (state: State) => fromShared.getSponsoredAgent(state.shared);
export const getSponsoredAirlineV2 = (state: State) =>
  fromShared.getSponsoredAirlineV2(state.shared);
export const isSponsoredAirlineV2FilterActive = (state: State) =>
  fromShared.isSponsoredAirlineV2FilterActive(state.shared);

// flightSearchResults - entities - legs
export const flightLeg = (state: State, id: string) => fromShared.flightLeg(state.shared, id);
export const flightAllLegs = (state: State) => fromShared.flightAllLegs(state.shared);
// flightSearchResults - entities - segments
export const flightSegment = (state: State, id: string) =>
  fromShared.flightSegment(state.shared, id);
export const flightAllSegments = (state: State) => fromShared.flightAllSegments(state.shared);

// flightSearchResults - entities - itineraries
export const flightItinerary = (state: State, id: string) =>
  fromShared.flightItinerary(state.shared, id);
export const flightAllItineraries = (state: State) => fromShared.flightAllItineraries(state.shared);
export const getItineraryFilterStatusMap = (state: State, id: string) =>
  fromShared.getItineraryFilterStatusMap(state.shared, id);

// flightSearchResults - entities - alliances
export const flightAlliance = (state: State, id: number) =>
  fromShared.flightAlliance(state.shared, id);
export const flightAllAlliances = (state: State) => fromShared.flightAllAlliances(state.shared);
